import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { SEVEN_GUAA_CLIP, announce, guaaPhrases, voiceSupported } from '../lib/voice';
import { Avatar, Name, teamAvatarFill } from './ui';

export interface GuaaPopupOptions {
  name: string;
  avatarKey: string | null;
  team: { name: string; color: string };
  /** Guaa just given in this deal (usually 1, or more with a Court). */
  guaa: number;
  isCourt: boolean;
  /** The team's new Guaa total, when this deal just hit a milestone (currently only 7). Otherwise null. */
  milestone: number | null;
}

interface Pending extends GuaaPopupOptions {
  id: number;
}

let pending: Pending | null = null;
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
let hostMounted = false;

/** A playful "got Guaa!" popup — the player's photo, a few mocking tears. Stays until dismissed. */
export function showGuaaPopup(options: GuaaPopupOptions) {
  if (!hostMounted) return; // decorative only; never block the game if the host isn't up
  pending = { ...options, id: nextId++ };
  emit();
}

function dismiss(id: number) {
  if (pending?.id === id) {
    pending = null;
    emit();
  }
}

/** Falling crying-emoji particles, scattered and staggered. */
const TEAR_SPOTS: { left: string; delay: string; duration: string; size: string }[] = [
  { left: '6%', delay: '0s', duration: '1.5s', size: 'text-xl' },
  { left: '20%', delay: '0.3s', duration: '1.8s', size: 'text-2xl' },
  { left: '38%', delay: '0.1s', duration: '1.4s', size: 'text-lg' },
  { left: '58%', delay: '0.5s', duration: '1.7s', size: 'text-2xl' },
  { left: '74%', delay: '0.2s', duration: '1.5s', size: 'text-xl' },
  { left: '88%', delay: '0.4s', duration: '1.9s', size: 'text-lg' },
];

/** Falling celebration/fireworks particles for the 7-Guaa milestone — bigger, denser, gaudier. */
const MILESTONE_SPOTS: { left: string; delay: string; duration: string; size: string; emoji: string }[] = [
  { left: '4%', delay: '0s', duration: '1.6s', size: 'text-2xl', emoji: '🎉' },
  { left: '16%', delay: '0.2s', duration: '1.3s', size: 'text-lg', emoji: '✨' },
  { left: '28%', delay: '0.5s', duration: '1.8s', size: 'text-3xl', emoji: '🎆' },
  { left: '42%', delay: '0.1s', duration: '1.4s', size: 'text-xl', emoji: '🔥' },
  { left: '56%', delay: '0.4s', duration: '1.7s', size: 'text-2xl', emoji: '🎊' },
  { left: '68%', delay: '0.15s', duration: '1.5s', size: 'text-lg', emoji: '✨' },
  { left: '80%', delay: '0.35s', duration: '1.9s', size: 'text-3xl', emoji: '🎆' },
  { left: '92%', delay: '0.25s', duration: '1.3s', size: 'text-xl', emoji: '🔥' },
];

/** Mount once near the app root. */
export function GuaaPopupHost() {
  const [current, setCurrent] = useState<Pending | null>(pending);
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    hostMounted = true;
    const sync = () => setCurrent(pending);
    listeners.add(sync);
    return () => {
      hostMounted = false;
      listeners.delete(sync);
    };
  }, []);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (current && !d.open) d.showModal();
    if (!current && d.open) d.close();
  }, [current]);

  const isMilestone = current?.milestone != null;

  return (
    <dialog
      ref={ref}
      aria-label={current ? (isMilestone ? `${current.team.name} reached ${current.milestone} Guaa!` : `${current.name} got ${current.guaa} Guaa`) : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (current) dismiss(current.id);
      }}
      onClick={(e) => e.target === ref.current && current && dismiss(current.id)}
      className="m-auto w-[min(22rem,calc(100%-2.5rem))] max-w-none overflow-hidden rounded-[2rem] bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      {current && (
        <div
          className={`animate-pop relative overflow-hidden rounded-[2rem] text-center text-white shadow-[0_30px_70px_-20px_rgb(0_0_0/0.8)] ring-2 ${
            isMilestone ? 'ring-amber-200/40' : 'ring-white/20'
          }`}
          style={{
            background: isMilestone
              ? 'linear-gradient(165deg, #f59e0b 0%, #e11d48 45%, #7c1d3a 80%, #3d1020 100%)'
              : 'linear-gradient(165deg, #c0264a 0%, #7c1d3a 55%, #3d1020 100%)',
          }}
        >
          {/* Falling particles, behind the content */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            {isMilestone
              ? MILESTONE_SPOTS.map((t, i) => (
                  <span
                    key={i}
                    className={`animate-guaa-fall absolute top-0 ${t.size} opacity-90`}
                    style={{ left: t.left, animationDelay: t.delay, animationDuration: t.duration } as CSSProperties}
                  >
                    {t.emoji}
                  </span>
                ))
              : TEAR_SPOTS.map((t, i) => (
                  <span
                    key={i}
                    className={`animate-guaa-fall absolute top-0 ${t.size} opacity-80`}
                    style={{ left: t.left, animationDelay: t.delay, animationDuration: t.duration } as CSSProperties}
                  >
                    😭
                  </span>
                ))}
          </div>

          <div className="relative px-6 pt-7 pb-6">
            <div className="absolute top-0 right-6 flex gap-2">
              {isMilestone && voiceSupported() && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    new Audio(SEVEN_GUAA_CLIP).play().catch(() => {});
                  }}
                  aria-label={`Play the ${current.milestone} Guaa sound`}
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/15 text-lg ring-1 ring-white/25 hover:bg-white/25"
                >
                  🎺
                </button>
              )}
              {voiceSupported() && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    announce(guaaPhrases(current.name, current.isCourt), { force: true });
                  }}
                  aria-label={`Say "${current.name} ne Guaaah khaya" in Hindi`}
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/15 text-lg ring-1 ring-white/25 hover:bg-white/25"
                >
                  🔊
                </button>
              )}
            </div>

            {isMilestone ? (
              <span className="animate-guaa-stamp inline-block rounded-lg bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 px-5 py-1.5 font-display text-2xl font-extrabold tracking-wider text-suit-red uppercase shadow-[0_0_24px_rgba(251,191,36,0.8)]">
                {current.milestone} Guaa!
              </span>
            ) : (
              <span className="animate-guaa-stamp inline-block rounded-lg bg-white px-4 py-1 text-xl font-extrabold tracking-wider text-suit-red uppercase shadow-lg">
                Guaa!
              </span>
            )}

            <div className="animate-guaa-shake relative mx-auto mt-5 w-fit">
              <Avatar
                name={current.name}
                avatarKey={current.avatarKey}
                size={isMilestone ? 116 : 104}
                ring={isMilestone ? '#fde68a' : '#fff'}
                fill={teamAvatarFill(current.team.color)}
              />
              {isMilestone ? (
                <>
                  <span className="absolute -top-3 -right-4 text-4xl drop-shadow" aria-hidden>
                    🏆
                  </span>
                  <span className="absolute -bottom-2 -left-4 text-3xl drop-shadow" aria-hidden>
                    🔥
                  </span>
                </>
              ) : (
                <>
                  <span className="absolute -top-2 -right-3 text-4xl drop-shadow" aria-hidden>
                    😭
                  </span>
                  <span className="absolute -bottom-1 -left-4 text-3xl drop-shadow" aria-hidden>
                    😢
                  </span>
                </>
              )}
            </div>

            <p className="mt-4 font-display text-2xl leading-tight font-extrabold">
              <Name>{current.name}</Name>
            </p>
            {isMilestone ? (
              <p className="mt-0.5 text-sm text-white/90">
                <span style={{ color: '#fde68a' }} className="font-bold">
                  {current.team.name}
                </span>{' '}
                just hit {current.milestone} Guaa — unlucky milestone! 🎊
              </p>
            ) : (
              <p className="mt-0.5 text-sm text-white/80">
                <span style={{ color: current.team.color }} className="font-bold">
                  {current.team.name}
                </span>{' '}
                take the hit
              </p>
            )}

            <p
              className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-display text-lg font-extrabold ${
                isMilestone ? 'bg-amber-900/40 ring-1 ring-amber-200/40' : 'bg-black/25'
              }`}
            >
              +{current.guaa} Guaa
              {current.isCourt && <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tracking-wide uppercase">Court</span>}
            </p>

            <button
              type="button"
              onClick={() => dismiss(current.id)}
              className="mt-5 block w-full rounded-xl bg-white/15 py-2.5 text-sm font-semibold text-white/90 ring-1 ring-white/25 hover:bg-white/25"
            >
              Tap to continue
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
